package com.iregained.site.components.sections.clinicians.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.clinicians.styles.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

// UNIQUE ANIMATION: Staggered Lift
@Composable
private fun Modifier.staggeredLift(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 40.px).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun CliniciansImplementationSection(data: CliniciansImplementationData = defaultCliniciansImplementationData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "clinicians-implementation-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.cards.isNotEmpty()) {
        Column(
            modifier = ImplementationContainerStyle.toModifier().id(targetId).fillMaxWidth()
                .backgroundColor(palette.custom.lightGray)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionTitle.isNotBlank()) SectionHeader(
                        text = data.sectionTitle,
                        fontSize = 32.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 16.px).staggeredLift(isVisible, 0),
                        alignment = Alignment.CenterHorizontally
                    )
                    if (data.sectionSubtitle.isNotBlank()) Div(
                        attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                            .fontWeight(FontWeight.Medium).color(palette.custom.darkText).textAlign(TextAlign.Center)
                            .margin(bottom = 40.px).staggeredLift(isVisible, 150).toAttrs {
                                ref { htmlElement ->
                                    htmlElement.innerHTML = data.sectionSubtitle; onDispose {}
                                }
                            })

                    SimpleGrid(numColumns(1, sm = 1, md = 3), modifier = ImplementationGridStyle.toModifier()) {
                        data.cards.forEachIndexed { index, card ->
                            key(card.title) {
                                ImplementationCard(card, Modifier.staggeredLift(isVisible, 300 + (index * 150)))
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ImplementationCard(card: ImplementationCardData, modifier: Modifier) {
    Column(modifier = ImplementationCardStyle.toModifier().then(modifier), horizontalAlignment = Alignment.Start) {
        if (card.iconPath.isNotBlank()) Image(src = card.iconPath, modifier = ImplementationCardIconStyle.toModifier())
        if (card.title.isNotBlank()) SpanText(text = card.title, modifier = ImplementationCardTitleStyle.toModifier())
        if (card.description.isNotBlank()) Div(
            attrs = ImplementationCardDescStyle.toModifier()
                .toAttrs { ref { htmlElement -> htmlElement.innerHTML = card.description; onDispose {} } })
        if (card.bulletPoints.isNotEmpty()) {
            Div(attrs = ImplementationListContainerStyle.toModifier().toAttrs()) {
                Ul(attrs = ImplementationUlStyle.toModifier().toAttrs()) {
                    card.bulletPoints.forEach { point ->
                        key(point) {
                            Li(attrs = ImplementationLiStyle.toModifier().toAttrs()) {
                                SpanText(
                                    text = point, modifier = ImplementationLiTextStyle.toModifier().fontSize(16.px)
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & IMAGE SPECS
// ==========================================
/**
 * CMS IMAGE UPLOAD SPECS FOR IMPLEMENTATION CARDS:
 * @field iconPath:
 * - Aspect Ratio: 1:1 (Square).
 * - Recommended Dimensions: 64x64px.
 * - Format: SVG (Highly Preferred) to ensure perfect scaling and no pixelation.
 * - Content: Monochromatic or bi-color line-art icons representing Setup, Training, and Support.
 */
data class ImplementationCardData(
    val iconPath: String, val title: String, val description: String, val bulletPoints: List<String>
)

data class CliniciansImplementationData(
    val sectionId: String? = null,
    val sectionTitle: String = "SEAMLESS INSTALLATION & INTEGRATION",
    val sectionSubtitle: String = "Deploy the MyHand<sup>®</sup> System with zero disruption to your daily workflow. We support your practice every step of the way.",
    val cards: List<ImplementationCardData> = listOf(
        ImplementationCardData(
            iconPath = Res.Images.CliniciansPage.INTEGRATION.PLUG_PLAY,
            title = "Setup",
            description = "",
            bulletPoints = listOf(
                "Intuitive 1-2 minute initial setup",
                "Only 3 primary adjustments: finger, thumb, and armrest",
                "Smart assessment protocol remembers patient parameters",
                "Quick-navigation application with pre-programmed, time-saving protocols"
            )
        ), ImplementationCardData(
            iconPath = Res.Images.CliniciansPage.INTEGRATION.SUPPORT,
            title = "Service",
            description = "",
            bulletPoints = listOf(
                "1-year standard warranty with extended plans available",
                "Guaranteed 48-hour turnaround on service and repair",
                "Flexible maintenance: on-site, hot-swaps, or loaner devices",
                "99.5% software uptime with a fast-response ticketing system",
                "24/7 emergency support for critical issues"
            )
        ), ImplementationCardData(
            iconPath = Res.Images.CliniciansPage.INTEGRATION.CERTIFICATION,
            title = "Training",
            description = "",
            bulletPoints = listOf(
                "Comprehensive online library of clinical and technical resources",
                "Built-in application tutorials and instructional videos",
                "Ongoing virtual training and educational webinars",
                "Continuous onboarding and refresher courses for clinical staff"
            )
        )
    )
)

val defaultCliniciansImplementationData = CliniciansImplementationData()